import React from 'react';
import { cards, spacing, shadows } from '../../styles/design-tokens';

/**
 * LoadingState Component
 * Skeleton loading states for different content types
 * 
 * @param {string} variant - Loading variant: 'card' | 'list' | 'table' | 'form' | 'text' | 'stat-card' | 'job-card' | 'candidate-card'
 * @param {number} count - Number of skeleton items to show
 * @param {string} className - Additional CSS classes
 */
const LoadingState = ({ 
  variant = 'card',
  count = 3,
  className = '',
}) => {
  // Skeleton animation class
  const animateClass = 'animate-pulse';
  
  // Skeleton variants
  const renderSkeleton = () => {
    switch (variant) {
      case 'stat-card':
        return (
          <div className={`${cards?.default} ${shadows?.sm} ${animateClass}`}>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-muted rounded-full flex-shrink-0"></div>
              <div className="flex-1">
                <div className="h-6 bg-muted rounded w-16 mb-2"></div>
                <div className="h-4 bg-muted rounded w-24"></div>
              </div>
            </div>
          </div>
        );
        
      case 'job-card':
        return (
          <div className={`${cards?.default} ${shadows?.sm} ${animateClass}`}>
            <div className="flex items-start gap-4 mb-4">
              <div className="h-12 w-12 bg-muted rounded-lg flex-shrink-0"></div>
              <div className="flex-1">
                <div className="h-5 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              <div className="h-3 bg-muted rounded w-full"></div>
              <div className="h-3 bg-muted rounded w-5/6"></div>
            </div>
            <div className="flex gap-2">
              <div className="h-6 bg-muted rounded-full w-20"></div>
              <div className="h-6 bg-muted rounded-full w-24"></div>
              <div className="h-6 bg-muted rounded-full w-20"></div>
            </div>
          </div>
        );
        
      case 'candidate-card':
        return (
          <div className={`${cards?.compact} ${shadows?.sm} ${animateClass}`}>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-muted rounded-full flex-shrink-0"></div>
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-32 mb-2"></div>
                <div className="h-3 bg-muted rounded w-24 mb-1"></div>
                <div className="h-3 bg-muted rounded w-40"></div>
              </div>
              <div className="flex gap-2">
                <div className="h-8 w-8 bg-muted rounded-md"></div>
                <div className="h-8 w-20 bg-muted rounded-md"></div>
              </div>
            </div>
          </div>
        );
      
      case 'card':
        return (
          <div className={`${cards?.default} ${shadows?.sm} ${animateClass}`}>
            <div className="h-4 bg-muted rounded w-3/4 mb-3"></div>
            <div className="h-3 bg-muted rounded w-full mb-2"></div>
            <div className="h-3 bg-muted rounded w-5/6 mb-4"></div>
            <div className="flex gap-2">
              <div className="h-8 bg-muted rounded w-20"></div>
              <div className="h-8 bg-muted rounded w-20"></div>
            </div>
          </div>
        );
      
      case 'list':
        return (
          <div className={`${cards?.compact} ${shadows?.sm} ${animateClass} flex items-center gap-3`}>
            <div className="h-10 w-10 bg-muted rounded-full flex-shrink-0"></div>
            <div className="flex-1">
              <div className="h-3 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-2 bg-muted rounded w-3/4"></div>
            </div>
          </div>
        );
      
      case 'table':
        return (
          <div className={`${animateClass} border-b border-border py-3`}>
            <div className="grid grid-cols-4 gap-4">
              <div className="h-3 bg-muted rounded"></div>
              <div className="h-3 bg-muted rounded"></div>
              <div className="h-3 bg-muted rounded"></div>
              <div className="h-3 bg-muted rounded w-20"></div>
            </div>
          </div>
        );
      
      case 'form':
        return (
          <div className={`${animateClass} ${spacing?.component}`}>
            <div className="h-3 bg-muted rounded w-1/4 mb-2"></div>
            <div className="h-10 bg-muted rounded w-full mb-4"></div>
            <div className="h-3 bg-muted rounded w-1/4 mb-2"></div>
            <div className="h-10 bg-muted rounded w-full"></div>
          </div>
        );
      
      case 'text':
        return (
          <div className={`${animateClass} space-y-2`}>
            <div className="h-3 bg-muted rounded w-full"></div>
            <div className="h-3 bg-muted rounded w-5/6"></div>
            <div className="h-3 bg-muted rounded w-4/6"></div>
          </div>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <div className={`flex flex-col ${spacing?.component} ${className}`?.trim()}>
      {Array.from({ length: count })?.map((_, index) => (
        <div key={index}>
          {renderSkeleton()}
        </div>
      ))}
    </div>
  );
};

export default LoadingState;